import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import TeethSelector from './TeethSelector';
import { Disclosure } from '@headlessui/react';
import { ChevronUpIcon } from '@heroicons/react/20/solid';
// Remove MUI Autocomplete/TextField
import { apiClient } from '../../services/apiClient';
import { format } from 'date-fns';
import { parseISO, isValid as isValidDate, format as formatDate } from 'date-fns';
import jsPDF from 'jspdf';
import lamalBg1 from '../../assets/lamal-fr-1.jpg';
import lamalBg2 from '../../assets/lamal-fr-2.jpg';
import lamalDe1 from '../../assets/lamal-de-1.png';
import lamalDe2 from '../../assets/lamal-de-2.png';
import lamalIt1 from '../../assets/lamal-it-1.png';
import lamalIt2 from '../../assets/lamal-it-2.png';
import colors from '../../config/colors';
import { normalizeTeethListFields, parseTeethList } from '../../utils/teethList';
import { shouldHideMissingTeethGridInPdf } from '../../constants/insuranceRoles';
import * as pdfjsLib from 'pdfjs-dist/build/pdf'; 
pdfjsLib.GlobalWorkerOptions.workerSrc = `/pdf.worker.min.mjs`;

const defaultFormData = {
  "patient-birth": "",
  "patient-address": "",
  "patient-name": "",
  "patient-assurance-nr": "",
  "employee-name": "",
  "phone-nr": "",
  "dentist-office": "",
  "dentist": "",
  "gln-nr": "",
  "rcc-nr": "",
  "accident-date": "",
  "examination-date": "",
  "accident-cause": "",
  "soft-damage": "",
  "prosthetic-damage": "",
  "missing-teeth": "",
  "teeth-lost": "",
  "teeth-moved": "",
  "teeth-moving": "",
  "teeth-hurt": "",
  "teeth-fract-without-lesion": "",
  "teeth-fract-with-lesion": "",
  "teeth-root-fract": "",
  "lamal-doctor-report": false,
  "lamal-number": "",
  "lamal-diagnostic": "",
  "lamal-article": "",
  "lamal-letter": "",
  "lamal-not-replaced": "",
  "lamal-prosthetic": "",
  "lamal-not-cured": "",
  "lamal-cured": "",
  "lamal-lesion": "",
  "measuring-others": "",
  "measuring-diagnostic": "",
  "interm-treatment-delay-definitive": false,
  "interm-treatment": "",
  "interm-treatment-delay-definitive-length": "",
  "observation-required": false,
  "observation-length": "",
  "interm-treatment-orthodontie": false,
  "definitive-treatment": "",
  "teeth-been-replaced": "",
  "include-quote": false
};

// Debounce utility
function debounce(fn, delay) {
  let timer = null;
  const debounced = (...args) => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => fn(...args), delay);
  };
  debounced.cancel = () => {
    if (timer) clearTimeout(timer);
    timer = null;
  };
  debounced.flush = (...args) => {
    if (timer) clearTimeout(timer);
    timer = null;
    fn(...args);
  };
  return debounced;
}

const InsuranceForm = forwardRef(({ initialData, onSave, selectedRole }, ref) => {
  const { t, i18n } = useTranslation('common');
  const [formData, setFormData] = useState(() => {
    return {
      ...defaultFormData,
      ...normalizeTeethListFields(initialData || {})
    };
  });
  const formDataRef = useRef(formData);
  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);

  const debouncedSave = useRef(
    debounce((data) => {
      if (typeof onSave === 'function') {
        onSave(data);
      }
    }, 500)
  ).current;

  // Sync formData with initialData before paint (avoids PDF/correction seeing stale local state)
  useLayoutEffect(() => {
    const next = {
      ...defaultFormData,
      ...normalizeTeethListFields(initialData || {})
    };
    setFormData((prev) => {
      if (JSON.stringify(prev) === JSON.stringify(next)) {
        return prev;
      }
      formDataRef.current = next;
      return next;
    });
  }, [initialData]);

  const flushSave = useCallback(() => {
    const snapshot = { ...formDataRef.current };
    if (typeof onSave === 'function') {
      debouncedSave.cancel();
      onSave(snapshot);
    }
    return snapshot;
  }, [onSave, debouncedSave]);

  useImperativeHandle(ref, () => ({
    getFormData: () => ({ ...formDataRef.current }),
    flushSave,
  }), [flushSave]);
  
  // Initialize all fields as touched to show errors immediately
  const [touchedFields, setTouchedFields] = useState(() => {
    return Object.keys(defaultFormData).reduce((acc, field) => {
      acc[field] = true;
      return acc;
    }, {});
  });

  const [patientOptions, setPatientOptions] = useState([]);
  const [isLoadingPatients, setIsLoadingPatients] = useState(false);
  const [patientSearch, setPatientSearch] = useState('');
  const [showPatientDropdown, setShowPatientDropdown] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  const handleChange = (field, value) => {
    setFormData((prev) => {
      const next = {
        ...prev,
        [field]: value
      };
      formDataRef.current = next;
      debouncedSave(next);
      return next;
    });
  };

  const handleBlur = (field) => {
    setTouchedFields(prev => ({
      ...prev,
      [field]: true
    }));
  };

  const isFieldInvalid = (field) => {
    return !formData[field];
  };

  const renderAccordionSection = ({ title, sectionKey, children, sectionFields = [], isOptional = false }) => {
    const hasEmptyFields = sectionFields.some(field => isFieldInvalid(field));
    const hasFilledFields = sectionFields.some(field => formData[field] && formData[field].length > 0);
    const allFieldsFilled = sectionFields.length > 0 && !hasEmptyFields;
    const isTeethSection = sectionKey === "teethState";

    return (
      <Disclosure>
        {({ open }) => (
          <div className={`mb-4 rounded-lg ${open ? 'bg-white shadow-md' : ''}`}>
            <Disclosure.Button 
              className={`flex w-full justify-between rounded-lg px-4 py-2 text-left text-sm font-medium
                bg-white text-gray-900 shadow-sm
                focus:outline-none focus-visible:ring focus-visible:ring-blue-500 focus-visible:ring-opacity-50
                hover:bg-gray-50 transition-colors duration-150`}
            >
              <span>{title}</span>
              <div className="flex items-center gap-2">
                {!isTeethSection && !isOptional && hasEmptyFields ? (
                  <span className="px-2 py-1 text-xs font-medium bg-red-50 text-red-600 rounded">
                    {t('insurance.labels.required')}
                  </span>
                ) : (isOptional ? hasFilledFields : allFieldsFilled) ? (
                  <span className="px-2 py-1 text-xs font-medium bg-green-50 text-green-600 rounded">
                    {t('insurance.labels.filled')}
                  </span>
                ) : null}
                <ChevronUpIcon 
                  className={`${open ? 'rotate-180 transform' : ''} h-5 w-5 text-gray-500 ml-2`}
                />
              </div>
            </Disclosure.Button>
            <Disclosure.Panel className="px-4 pt-4 pb-2 text-sm text-gray-600">
              {children}
            </Disclosure.Panel>
          </div>
        )}
      </Disclosure>
    );
  };

  const renderField = (field, label, type = "text", required = true) => {
    const isInvalid = required && isFieldInvalid(field);
    return (
      <div className="flex flex-col space-y-1">
        {label && (
          <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
            {label}
            {required && <span className="text-red-500">*</span>}
            {isInvalid && (
              <span className="text-xs text-red-600">
                {t('insurance.labels.required')}
              </span>
            )}
          </label>
        )}
        {type === "textarea" ? (
          <textarea
            value={formData[field]}
            onChange={(e) => handleChange(field, e.target.value)}
            onBlur={() => handleBlur(field)}
            required={required}
            rows={4}
            className={`rounded-md shadow-sm focus:ring focus:ring-opacity-50 mt-1 block w-full ${
              isInvalid 
                ? 'border-red-300 focus:border-red-300 focus:ring-red-200' 
                : 'border-gray-300 focus:border-blue-300 focus:ring-blue-200'
            }`}
          />
        ) : (
          <input
            type={type}
            value={formData[field]}
            onChange={(e) => handleChange(field, e.target.value)}
            onBlur={() => handleBlur(field)}
            required={required}
            className={`rounded-md shadow-sm focus:ring focus:ring-opacity-50 ${
              isInvalid 
                ? 'border-red-300 focus:border-red-300 focus:ring-red-200' 
                : 'border-gray-300 focus:border-blue-300 focus:ring-blue-200'
            }`}
          />
        )}
      </div>
    );
  };

  // Define the fields for each section
  const sectionFields = {
    administrative: [
      'dentist-office', 'dentist', 'gln-nr', 'rcc-nr', 'phone-nr', 'employee-name',
      'patient-assurance-nr', 'patient-name', 'patient-address', 'patient-birth'
    ],
    teethState: ['missing-teeth'],
    accident: [
      'accident-date', 'examination-date', 'accident-cause'
    ],
    damage: [
      'soft-damage', 'prosthetic-damage', 'missing-teeth', 'teeth-lost',
      'teeth-moved', 'teeth-moving', 'teeth-hurt', 'teeth-fract-without-lesion',
      'teeth-fract-with-lesion', 'teeth-root-fract'
    ],
    lamal: [
      'lamal-doctor-report', 'lamal-number', 'lamal-diagnostic', 'lamal-article',
      'lamal-letter', 'lamal-prosthetic', 'lamal-not-replaced', 'lamal-not-cured',
      'lamal-cured', 'lamal-lesion'
    ],
    treatment: [
      'mesuring-others', 'mesuring-diagnostic', 'interm-treatment-delay-definitive',
      'interm-treatment', 'interm-treatment-delay-definitive-length', 'observation-required',
      'observation-length', 'interm-treatment-orthodontie', 'definitive-treatment',
      'teeth-been-replaced', 'measuring-diagnostic', 'measuring-others', 'include-quote'
    ]
  };

  const handleTeethSelection = (selectedTeeth) => {
    handleChange('missing-teeth', selectedTeeth);
  };

  // Patient search handler
  const fetchPatients = async (search) => {
    setIsLoadingPatients(true);
    try {
      const patients = await apiClient.get(`/api/patients?search=${encodeURIComponent(search)}`);
      setPatientOptions(patients || []);
    } catch (e) {
      setPatientOptions([]);
    } finally {
      setIsLoadingPatients(false);
    }
  };
  const debouncedFetchPatients = debounce(fetchPatients, 300);

  const handlePatientSelect = (patient) => {
    if (patient) {
      handleChange('patient-name', patient.name);
      handleChange('patient-assurance-nr', patient.assurance_number || '');
      handleChange('patient-address', patient.address || '');
      let birth = patient.birthdate || '';
      if (birth) {
        let dateObj = typeof birth === 'string' ? parseISO(birth) : birth;
        if (isValidDate(dateObj)) {
          birth = formatDate(dateObj, 'yyyy-MM-dd');
        } else {
          birth = '';
        }
      }
      handleChange('patient-birth', birth);
      setShowPatientDropdown(false);
      setPatientSearch(patient.name);
    }
  };

  // Handle input changes for patient search
  const handlePatientSearchChange = (e) => {
    const value = e.target.value;
    setPatientSearch(value);
    if (value.length > 1) {
      debouncedFetchPatients(value);
      setShowPatientDropdown(true);
    } else {
      setShowPatientDropdown(false);
      setPatientOptions([]);
    }
  };

  // Keyboard navigation for dropdown
  const handlePatientSearchKeyDown = (e) => {
    if (!showPatientDropdown || patientOptions.length === 0) return;
    if (e.key === 'ArrowDown') {
      setHighlightedIndex((prev) => (prev + 1) % patientOptions.length);
      e.preventDefault();
    } else if (e.key === 'ArrowUp') {
      setHighlightedIndex((prev) => (prev - 1 + patientOptions.length) % patientOptions.length);
      e.preventDefault();
    } else if (e.key === 'Enter' && highlightedIndex >= 0) {
      handlePatientSelect(patientOptions[highlightedIndex]);
      e.preventDefault();
    } else if (e.key === 'Escape') {
      setShowPatientDropdown(false);
    }
  };

  // Utility to wrap text for PDF
  const wrapText = (doc, text, x, y, maxWidth, lineHeight, fontSize) => {
    doc.setFontSize(fontSize);
    const lines = doc.splitTextToSize(text, maxWidth);
    lines.forEach((line, i) => {
      doc.text(line, x, y + i * lineHeight);
    });
    return y + lines.length * lineHeight;
  };

  // PDF field positions
  const defaultPdfFields = [
    // Page 1
    [
      { key: 'patient-assurance-nr', x: 140, y: 57, maxWidth: 120, fontSize: 7 },
      { key: 'patient-name', x: 100, y: 66, maxWidth: 180, fontSize: 7 },
      { key: 'patient-address', x: 82, y: 75, maxWidth: 220, fontSize: 7 },
      { key: 'patient-birth', x: 79, y: 84, maxWidth: 80, fontSize: 7 },
      { key: 'dentist-office', x: 110, y: 102, maxWidth: 120, fontSize: 7 },
      { key: 'dentist', x: 138, y: 111, maxWidth: 120, fontSize: 7 },
      { key: 'gln-nr', x: 80, y: 120, maxWidth: 100, fontSize: 7 },
      { key: 'phone-nr', x: 319, y: 93, maxWidth: 100, fontSize: 7 },
      { key: 'rcc-nr', x: 80, y: 129, maxWidth: 100, fontSize: 7 },
      { key: 'employee-name', x: 279, y: 111, maxWidth: 120, fontSize: 7 },
      { key: 'accident-date', x: 117, y: 220, maxWidth: 80, fontSize: 7 },
      { key: 'examination-date', x: 271, y: 220, maxWidth: 80, fontSize: 7 },
      { key: 'accident-cause', x: 120, y: 235, maxWidth: 220, fontSize: 7 },
      { key: 'soft-damage', x: 161, y: 375, maxWidth: 420, fontSize: 7 },
      { key: 'prosthetic-damage', x: 197, y: 406, maxWidth: 420, fontSize: 7 },
      { key: 'lamal-diagnostic', x: 134, y: 441, maxWidth: 420, fontSize: 7 },
      { key: 'lamal-article', x: 160, y: 455, maxWidth: 100, fontSize: 7 },
      { key: 'lamal-letter', x: 220, y: 455, maxWidth: 60, fontSize: 7 },
      { key: 'lamal-number', x: 270, y: 455, maxWidth: 60, fontSize: 7 },
      { key: 'lamal-prosthetic', x: 65, y: 581, maxWidth: 330, fontSize: 7 },
    ],
    // Page 2
    [
      { key: 'measuring-diagnostic', x: 50, y: 72, maxWidth: 350, fontSize: 7 },
      { key: 'measuring-others', x: 50, y: 100, maxWidth: 350, fontSize: 7 },
      { key: 'observation-length', x: 180, y: 148, maxWidth: 420, fontSize: 7 },
      { key: 'interm-treatment-delay-definitive-length', x: 325, y: 170, maxWidth: 420, fontSize: 7 },
      { key: 'interm-treatment', x: 50, y: 181, maxWidth: 350, fontSize: 7 },
      { key: 'definitive-treatment', x: 50, y: 250, maxWidth: 350, fontSize: 7 },
    ]
  ];


  const uploadedPdfFields = [
    // Page 1
    [
      { key: 'accident-date', x: 125, y: 206, maxWidth: 80, fontSize: 7 },
      { key: 'examination-date', x: 271, y: 206, maxWidth: 80, fontSize: 7 },
      { key: 'accident-cause', x: 125, y: 213, maxWidth: 220, fontSize: 7 },
      { key: 'soft-damage', x: 161, y: 369, maxWidth: 420, fontSize: 7 },
      { key: 'prosthetic-damage', x: 197, y: 398, maxWidth: 420, fontSize: 7 },
      { key: 'lamal-diagnostic', x: 114, y: 431, maxWidth: 420, fontSize: 7 },
      { key: 'lamal-article', x: 140, y: 447, maxWidth: 100, fontSize: 7 },
      { key: 'lamal-letter', x: 200, y: 447, maxWidth: 60, fontSize: 7 },
      { key: 'lamal-number', x: 250, y: 447, maxWidth: 60, fontSize: 7 },
      { key: 'lamal-prosthetic', x: 65, y: 570, maxWidth: 330, fontSize: 7 },
    ],
    // Page 2
    [
      { key: 'measuring-diagnostic', x: 50, y: 57, maxWidth: 350, fontSize: 7 },
      { key: 'measuring-others', x: 50, y: 78, maxWidth: 350, fontSize: 7 },
      { key: 'observation-length', x: 180, y: 117, maxWidth: 420, fontSize: 7 },
      { key: 'interm-treatment-delay-definitive-length', x: 330, y: 138, maxWidth: 420, fontSize: 7 },
      { key: 'interm-treatment', x: 50, y: 148, maxWidth: 350, fontSize: 7 },
      { key: 'definitive-treatment', x: 50, y: 200, maxWidth: 350, fontSize: 7 },
    ]
  ];

  // --- Default tooth positions ---
  const defaultToothPositions = {
    '18': { x: 144, y: 163 }, 
    '17': { x: 152, y: 163 }, 
    '16': { x: 160, y: 163 }, 
    '15': { x: 168, y: 163 },
    '14': { x: 177, y: 163 }, 
    '13': { x: 185, y: 163 }, 
    '12': { x: 193, y: 163 }, 
    '11': { x: 201, y: 163 },

    '48': { x: 144, y: 174 }, 
    '47': { x: 152, y: 174 }, 
    '46': { x: 160, y: 174 }, 
    '45': { x: 168, y: 174 },
    '44': { x: 177, y: 174 }, 
    '43': { x: 185, y: 174 }, 
    '42': { x: 193, y: 174 }, 
    '41': { x: 201, y: 174 },

    '21': { x: 213, y: 163 }, 
    '22': { x: 221, y: 163 }, 
    '23': { x: 229, y: 163 }, 
    '24': { x: 238, y: 163 },
    '25': { x: 247, y: 163 }, 
    '26': { x: 255, y: 163 },
    '27': { x: 264, y: 163 }, 
    '28': { x: 272, y: 163 }, 

    '31': { x: 213, y: 174 },
    '32': { x: 221, y: 174 }, 
    '33': { x: 229, y: 174 },
    '34': { x: 238, y: 174 }, 
    '35': { x: 247, y: 174 }, 
    '36': { x: 255, y: 174 }, 
    '37': { x: 264, y: 174 },
    '38': { x: 272, y: 174 }, 

    '55': { x: 295, y: 163 }, 
    '54': { x: 303, y: 163 }, 
    '53': { x: 311, y: 163 },
    '52': { x: 319, y: 163 }, 
    '51': { x: 328, y: 163 }, 

    '61': { x: 340, y: 163 }, 
    '62': { x: 349, y: 163 }, 
    '63': { x: 357, y: 163 },
    '64': { x: 365, y: 163 }, 
    '65': { x: 374, y: 163 }, 

    '85': { x: 295, y: 174 }, 
    '84': { x: 303, y: 174 }, 
    '83': { x: 311, y: 174 },
    '82': { x: 319, y: 174 }, 
    '81': { x: 328, y: 174 }, 

    '71': { x: 340, y: 174 }, 
    '72': { x: 349, y: 174 }, 
    '73': { x: 357, y: 174 },
    '74': { x: 365, y: 174 }, 
    '75': { x: 374, y: 174 }, 
  };

  // --- Uploaded PDF tooth positions (to be filled by you) ---
  const uploadedToothPositions = {
    '18': { x: 154, y: 156 }, 
    '17': { x: 162, y: 156 }, 
    '16': { x: 170, y: 156 }, 
    '15': { x: 178, y: 156 },
    '14': { x: 187, y: 156 }, 
    '13': { x: 196, y: 156 }, 
    '12': { x: 204, y: 156 }, 
    '11': { x: 212, y: 156 },

    '48': { x: 154, y: 170 }, 
    '47': { x: 162, y: 170 }, 
    '46': { x: 170, y: 170 }, 
    '45': { x: 178, y: 170 },
    '44': { x: 187, y: 170 }, 
    '43': { x: 195, y: 170 }, 
    '42': { x: 203, y: 170 }, 
    '41': { x: 211, y: 170 },

    '21': { x: 223, y: 156 }, 
    '22': { x: 231, y: 156 }, 
    '23': { x: 239, y: 156 }, 
    '24': { x: 248, y: 156 },
    '25': { x: 257, y: 156 }, 
    '26': { x: 265, y: 156 },
    '27': { x: 273, y: 156 }, 
    '28': { x: 281, y: 156 }, 

    '31': { x: 223, y: 170 },
    '32': { x: 231, y: 170 }, 
    '33': { x: 239, y: 170 },
    '34': { x: 248, y: 170 }, 
    '35': { x: 257, y: 170 }, 
    '36': { x: 265, y: 170 }, 
    '37': { x: 273, y: 170 },
    '38': { x: 281, y: 170 }, 

    '55': { x: 307, y: 156 }, 
    '54': { x: 315, y: 156 }, 
    '53': { x: 323, y: 156 },
    '52': { x: 331, y: 156 }, 
    '51': { x: 340, y: 156 }, 

    '61': { x: 351, y: 156 }, 
    '62': { x: 360, y: 156 }, 
    '63': { x: 367, y: 156 },
    '64': { x: 375, y: 156 }, 
    '65': { x: 384, y: 156 }, 

    '85': { x: 307, y: 170 }, 
    '84': { x: 315, y: 170 }, 
    '83': { x: 323, y: 170 },
    '82': { x: 331, y: 170 }, 
    '81': { x: 340, y: 170 }, 

    '71': { x: 351, y: 170 }, 
    '72': { x: 360, y: 170 }, 
    '73': { x: 367, y: 170 },
    '74': { x: 375, y: 170 }, 
    '75': { x: 384, y: 170 }, 
  };

  // --- Default teethBeenReplacedPositions ---
  const defaultTeethBeenReplacedPositions = {
    '18': { x: 87, y: 386 }, 
    '17': { x: 89, y: 375 }, 
    '16': { x: 90, y: 362 }, 
    '15': { x: 93, y: 351 },
    '14': { x: 99, y: 342 }, 
    '13': { x: 105, y: 332 }, 
    '12': { x: 114, y: 327 }, 
    '11': { x: 122, y: 324 },

    '48': { x: 260, y: 328 }, 
    '47': { x: 263, y: 341 }, 
    '46': { x: 268, y: 355 }, 
    '45': { x: 271, y: 366 },
    '44': { x: 277, y: 373 }, 
    '43': { x: 283, y: 383 }, 
    '42': { x: 291, y: 385 }, 
    '41': { x: 298, y: 386 },

    '21': { x: 132, y: 324 }, 
    '22': { x: 141, y: 327 }, 
    '23': { x: 149, y: 332 }, 
    '24': { x: 155, y: 342 },
    '25': { x: 161, y: 351 }, 
    '26': { x: 165, y: 362 },
    '27': { x: 169, y: 375 }, 
    '28': { x: 170, y: 386 }, 

    '31': { x: 307, y: 386 },
    '32': { x: 314, y: 385 }, 
    '33': { x: 322, y: 382 },
    '34': { x: 328, y: 374 }, 
    '35': { x: 333, y: 366 }, 
    '36': { x: 337, y: 355 }, 
    '37': { x: 341, y: 341 },
    '38': { x: 344, y: 328 }, 

    '55': { x: 107, y: 385 }, 
    '54': { x: 110, y: 374 }, 
    '53': { x: 112, y: 364 },
    '52': { x: 118, y: 359 }, 
    '51': { x: 123, y: 355 }, 

    '61': { x: 133, y: 355 }, 
    '62': { x: 138, y: 359 }, 
    '63': { x: 144, y: 364 },
    '64': { x: 146, y: 374 }, 
    '65': { x: 149, y: 385 }, 

    '85': { x: 280, y: 325 }, 
    '84': { x: 283, y: 336 }, 
    '83': { x: 287, y: 346 },
    '82': { x: 292, y: 353 }, 
    '81': { x: 296, y: 355 }, 

    '71': { x: 306, y: 355 }, 
    '72': { x: 311, y: 351 }, 
    '73': { x: 317, y: 346 },
    '74': { x: 319, y: 336 }, 
    '75': { x: 323, y: 325 }, 
  };

  // --- Uploaded PDF teethBeenReplacedPositions (to be filled by you) ---
  const uploadedTeethBeenReplacedPositions = {
    '18': { x: 87, y: 319 }, 
    '17': { x: 89, y: 308 }, 
    '16': { x: 90, y: 295 }, 
    '15': { x: 93, y: 284 },
    '14': { x: 99, y: 275 }, 
    '13': { x: 105, y: 265 }, 
    '12': { x: 114, y: 260 }, 
    '11': { x: 122, y: 257 },

    '48': { x: 265, y: 261 }, 
    '47': { x: 268, y: 274 }, 
    '46': { x: 273, y: 288 }, 
    '45': { x: 276, y: 299 },
    '44': { x: 282, y: 306 }, 
    '43': { x: 288, y: 316 }, 
    '42': { x: 296, y: 318 }, 
    '41': { x: 303, y: 319 },

    '21': { x: 133, y: 257 }, 
    '22': { x: 142, y: 260 }, 
    '23': { x: 150, y: 265 }, 
    '24': { x: 156, y: 275 },
    '25': { x: 162, y: 284 }, 
    '26': { x: 166, y: 295 },
    '27': { x: 170, y: 308 }, 
    '28': { x: 171, y: 319 }, 

    '31': { x: 312, y: 319 },
    '32': { x: 320, y: 318 }, 
    '33': { x: 327, y: 315 },
    '34': { x: 333, y: 307 }, 
    '35': { x: 338, y: 299 }, 
    '36': { x: 343, y: 288 }, 
    '37': { x: 347, y: 274 },
    '38': { x: 350, y: 261 }, 

    '55': { x: 107, y: 318 }, 
    '54': { x: 110, y: 307 }, 
    '53': { x: 112, y: 297 },
    '52': { x: 118, y: 292 }, 
    '51': { x: 123, y: 288 }, 

    '61': { x: 133, y: 288 }, 
    '62': { x: 138, y: 292 }, 
    '63': { x: 144, y: 297 },
    '64': { x: 146, y: 307 }, 
    '65': { x: 149, y: 318 }, 

    '85': { x: 285, y: 258 }, 
    '84': { x: 288, y: 269 }, 
    '83': { x: 292, y: 279 },
    '82': { x: 297, y: 286 }, 
    '81': { x: 303, y: 288 }, 

    '71': { x: 311, y: 288 }, 
    '72': { x: 317, y: 284 }, 
    '73': { x: 323, y: 279 },
    '74': { x: 325, y: 269 }, 
    '75': { x: 328, y: 258 }, 
  };

  function drawMissingTeethCrosses(doc, missingTeethString) {
    if (!missingTeethString) return;
    const teeth = parseTeethList(missingTeethString);
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0); // Black cross
    teeth.forEach(tooth => {
      const pos = toothPositions[tooth];
      if (pos) {
        doc.text('X', pos.x, pos.y, { align: 'center', baseline: 'middle' });
      }
    });
    doc.setTextColor(0, 0, 0); // Reset to black
  }

  const handleGeneratePDF = async () => {
    // Snapshot + flush parent so PDF/history never use a stale closure mid-await
    const data = flushSave();

    const doc = new jsPDF({ unit: 'px', format: 'a4' });
    // Load images as base64
    const getImageBase64 = (src) => {
      return new Promise((resolve) => {
        const img = new window.Image();
        img.crossOrigin = 'Anonymous';
        img.onload = function () {
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0);
          resolve(canvas.toDataURL('image/jpeg'));
        };
        img.src = src;
      });
    };

    const detectImageType = (src) => {
      if (typeof src === 'string' && src.toLowerCase().endsWith('.png')) return 'PNG';
      // data URLs from pdf extraction are JPEG
      if (typeof src === 'string' && src.startsWith('data:image/')) {
        return src.toLowerCase().includes('png') ? 'PNG' : 'JPEG';
      }
      return 'JPEG';
    };

    const currentLang = (i18n.language || 'fr').split('-')[0];
    const localizedDefaultBgs = currentLang === 'de'
      ? [lamalDe1, lamalDe2]
      : currentLang === 'it'
        ? [lamalIt1, lamalIt2]
        : [lamalBg1, lamalBg2];

    const bgs = adminPdfBg1 && adminPdfBg2 ? [adminPdfBg1, adminPdfBg2] : localizedDefaultBgs;

    // Fetch employee signature image once
    let signatureDataUrl = null;
    let signatureType = 'JPEG';
    let clinic = '';
    try {
      const profile = await apiClient.get('/api/profile');
      if (profile?.signature_image) {
        signatureDataUrl = profile.signature_image;
        signatureType = signatureDataUrl.startsWith('data:image/png') ? 'PNG' : 'JPEG';
      }
      clinic = (await apiClient.get('/api/clinic'))?.place;
    } catch (e) {
      // ignore
    }

    const hideMissingTeethGrid = shouldHideMissingTeethGridInPdf(selectedRole);

    [0, 1].forEach((pageIdx) => {
      if (pageIdx > 0) doc.addPage();
      const imgType = detectImageType(bgs[pageIdx]);
      doc.addImage(bgs[pageIdx], imgType, 0, 0, doc.internal.pageSize.getWidth(), doc.internal.pageSize.getHeight());
      // Draw missing teeth crosses on page 1 only
      if (pageIdx === 0) {
        drawMissingTeethCrosses(doc, data['missing-teeth']);
        // Draw teeth numbers grid for relevant fields
        const teethFields = [
          'teeth-lost',
          'teeth-moved',
          'teeth-moving',
          'teeth-hurt',
          'teeth-fract-without-lesion',
          'teeth-fract-with-lesion',
          'teeth-root-fract',
          'missing-teeth',
          'lamal-not-cured',
          'lamal-cured',
          'lamal-lesion',
        ];
        let gridY = isUploadedPdf ? 250 : 271;
        doc.setFontSize(7);
        doc.setTextColor(0, 0, 0);
        teethFields.forEach((field) => {
          if (field === 'missing-teeth') {
            gridY = isUploadedPdf ? 490 : 497.5;
          }
          if (field === 'teeth-moved' && isUploadedPdf) {
            gridY = 270;
          }
          if (field === 'teeth-fract-with-lesion' && isUploadedPdf) {
            gridY -= 2;
          }

          if (field === 'lamal-cured' && isUploadedPdf) {
            gridY -= 3;
          }

          doc.setFillColor(255, 255, 255);
          if (isUploadedPdf) {
            doc.rect(115, 198, 10, 10, 'F');
            doc.rect(117, 206, 10, 10, 'F');
            doc.rect(258, 198, 10, 10, 'F');
            doc.rect(22, 375, 10, 10, 'F');
            doc.rect(63, 369, 10, 10, 'F');
            doc.rect(63, 396, 10, 10, 'F');
            doc.rect(63, 560, 10, 10, 'F');
          }

          const value = data[field] || '';
          const shouldDrawTeethNumbers = value && !(field === 'missing-teeth' && hideMissingTeethGrid);
          if (shouldDrawTeethNumbers) {
            const teeth = parseTeethList(value);
            teeth.forEach((tooth) => {
              const pos = toothPositions[tooth];
              if (pos) {
                const toothNumber = Number(tooth);
                let yPos, xPos;
                if ((toothNumber > 10 && toothNumber < 30) || (toothNumber > 50 && toothNumber < 70)) {
                  yPos = Math.floor(gridY);
                } else {
                  yPos = Math.floor(gridY+7);
                }
                if (toothNumber > 50) {
                  xPos = pos.x - (isUploadedPdf ? 62 : 34);
                } else {
                  xPos = pos.x + (isUploadedPdf ? 67 : 93);
                }
                doc.text(tooth, xPos, yPos);
              }
            });
          }
          // Always reserve 2 lines per entry, but only use one for numbers
          gridY += isUploadedPdf ? 17 : 13.5;
        });
        // Draw doctor report white rectangle
        const doctorReport = data['lamal-doctor-report'];
        let rectX;
        if (isUploadedPdf) {
          rectX = doctorReport ? 372 : 360;
        } else {
          rectX = doctorReport ? 377 : 364;
        }
        const rectY = isUploadedPdf ? 442 : 450;
        doc.setFillColor(255, 255, 255);
        if (isUploadedPdf) {
          doc.rect(rectX, rectY, 14, 6, 'F');
        } else {
          doc.rect(rectX, rectY, 16, 6, 'F');
        }


      }
      if (pageIdx === 1) {

        doc.setFillColor(255, 255, 255);
        if (isUploadedPdf) {
          doc.rect(47, 47, 10, 10, 'F');
          doc.rect(47, 68, 10, 10, 'F');
          doc.rect(47, 138, 10, 10, 'F');
          doc.rect(47, 190, 10, 10, 'F');
          doc.rect(330, 130, 10, 10, 'F');
          if (data['observation-length']?.length > 2) {
            doc.rect(175, 109, 100, 10, 'F');
          } else {
            doc.rect(175, 109, 10, 10, 'F');
          }
        } else {
          if (data['observation-length']?.length > 2) {
            doc.rect(170, 140, 100, 10, 'F');
          }

          doc.rect(49, 178, 350, 40, 'F');
          doc.rect(49, 243, 350, 40, 'F');
        }
        // Draw X for boolean checkboxes
        if (data['observation-required']) {
          doc.setFontSize(10);
          doc.text('X', 49, isUploadedPdf ? 117 : 149);
        }
        if (data['interm-treatment-orthodontie']) {
          doc.setFontSize(10);
          doc.text('X', 49, isUploadedPdf ? 124 : 156);
        }
        if (data['interm-treatment-delay-definitive']) {
          doc.setFontSize(10);
          doc.text('X', 49, isUploadedPdf ? 138 : 170);
        }
        // Draw white rectangle at (44, 429) with width 358 and height 142
        doc.setFillColor(255, 255, 255);
        if (isUploadedPdf) {
          doc.rect(33, 350, 378, 200, 'F');
        } else {
          doc.rect(44, 429, 358, 142, 'F');
        }
        // Draw label if definitive-treatment is set and include-quote is checked
        if (data['definitive-treatment'] && data['include-quote']) {
          const label = t('insurance.fields.attachedQuoteLabel');
          doc.setFontSize(12);
          doc.setTextColor(0, 0, 0);
          // Center horizontally and vertically in the rectangle
          const centerX = isUploadedPdf ? 33 + 378 / 2 : 44 + 358 / 2;
          const centerY = isUploadedPdf ? 350 + 200 / 2 + 4 : 429 + 142 / 2 + 4; // +4 for visual vertical centering
          doc.text(label, centerX, centerY, { align: 'center', baseline: 'middle' });
        }

        // Draw employee signature/stamp image at bottom-right if available, with place/date label to its left
        if (signatureDataUrl) {
          // Fetch clinic place for label
          let placeLabel = '';
          try {
            const place = clinic;
            // Localized "place, le date" label
            const today = new Date();
            const formatted = today.toLocaleDateString((i18n.language || 'fr'), { year: 'numeric', month: '2-digit', day: '2-digit' });
            const localizedLe = t('insurance.labels.placeDateConnector', 'le');
            placeLabel = place ? `${place}, ${localizedLe} ${formatted}` : `${localizedLe} ${formatted}`;
          } catch (e) {}

          // Draw label
          doc.setFontSize(10);
          doc.setTextColor(0, 0, 0);
          // Position close to signature area
          const labelX = 90;
          const labelY = 590;
          if (placeLabel) {
            doc.text(placeLabel, labelX, labelY);
          }

          // Signature image
          const targetWidth = 60; // px
          const targetHeight = 20; // px
          const x = 320;
          const y = 573;
          doc.addImage(signatureDataUrl, signatureType, x, y, targetWidth, targetHeight);
        }
        // Draw squares for teeth-been-replaced (page 2 only)
        const replacedValue = data['teeth-been-replaced'] || '';
        if (replacedValue) {
          const replacedTeeth = parseTeethList(replacedValue);
          replacedTeeth.forEach((tooth) => {
            const pos = teethBeenReplacedPositions[tooth];
            if (pos) {
              doc.setFillColor(0, 0, 0);
              doc.rect(pos.x, pos.y, 5, 5, 'F');
            }
          });
        }
      }
      pdfFields[pageIdx].forEach(({ key, x, y, maxWidth, fontSize }) => {
        const value = data[key] || '';
        if (value) {
          if (key === 'soft-damage') {
            // Custom split for soft-damage
            const limit = 110;
            if (value.length > limit) {
              let splitIdx = value.lastIndexOf(' ', limit);
              if (splitIdx === -1) splitIdx = limit; // fallback: hard split
              const first = value.slice(0, splitIdx);
              const second = value.slice(splitIdx).trim();
              doc.setFontSize(fontSize);
              doc.text(first, x, y);
              if (second) {
                doc.text(second, 65, y + 11);
              }
            } else {
              doc.setFontSize(fontSize);
              doc.text(value, x, y);
            }
          } else if (key === 'prosthetic-damage') {
            // Custom split for prosthetic-damage
            const limit = 92;
            if (value.length > limit) {
              let splitIdx = value.lastIndexOf(' ', limit);
              if (splitIdx === -1) splitIdx = limit; // fallback: hard split
              const first = value.slice(0, splitIdx);
              const second = value.slice(splitIdx).trim();
              doc.setFontSize(fontSize);
              doc.text(first, x, y);
              if (second) {
                doc.text(second, 65, y + 11);
              }
            } else {
              doc.setFontSize(fontSize);
              doc.text(value, x, y);
            }
          } else {
            wrapText(doc, value.toString(), x, y, maxWidth, fontSize + 4, fontSize);
          }
        }
      });
    });
    doc.save('insurance-form.pdf');
  };

  const [adminPdfName, setAdminPdfName] = useState('');
  const [adminPdfFile, setAdminPdfFile] = useState(null);
  const [adminPdfBg1, setAdminPdfBg1] = useState(null);
  const [adminPdfBg2, setAdminPdfBg2] = useState(null);

  // Extract first two pages of PDF as images
  const extractPdfImages = async (file) => {
    const arrayBuffer = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
    const images = [];
    for (let i = 1; i <= 2; i++) {
      if (i > pdf.numPages) break;
      const page = await pdf.getPage(i);
      const viewport = page.getViewport({ scale: 2 }); // Adjust scale as needed
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      await page.render({ canvasContext: context, viewport }).promise;
      images.push(canvas.toDataURL('image/jpeg', 1.0));
    }
    return images;
  };

  // Update the PDF upload handler
  const handleAdminPdfUpload = async (e) => {
    const file = e.target.files[0];
    setAdminPdfFile(file);
    setAdminPdfName(file.name);
    const [bg1, bg2] = await extractPdfImages(file);
    setAdminPdfBg1(bg1);
    setAdminPdfBg2(bg2);
  };

  // Helper pour savoir si la section administrative est remplie
  const isAdminSectionFilled = sectionFields.administrative.some(field => formData[field] && formData[field].length > 0);

  // Patch renderAccordionSection pour la section administrative
  const patchedRenderAccordionSection = (props) => {
    if (props.sectionKey === 'administrative') {
      // Force le badge "filled" si un PDF est uploadé
      return renderAccordionSection({
        ...props,
        sectionFields: sectionFields.administrative,
        isOptional: !!adminPdfFile,
        children: props.children,
        hasFilledFields: isAdminSectionFilled,

      });
    }
    return renderAccordionSection(props);
  };

  const isUploadedPdf = !!adminPdfFile;
  const pdfFields = isUploadedPdf ? uploadedPdfFields : defaultPdfFields;
  const toothPositions = isUploadedPdf ? uploadedToothPositions : defaultToothPositions;
  const teethBeenReplacedPositions = isUploadedPdf ? uploadedTeethBeenReplacedPositions : defaultTeethBeenReplacedPositions;

  return (
    <form className="space-y-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold">{t('consultation.steps.insurance')}</h2>
        <button
          type="button"
          onClick={handleGeneratePDF}
          style={{
            backgroundColor: colors.primary.selected,
            color: 'white',
            borderRadius: '0.375rem',
            padding: '0.5rem 1rem',
            display: 'flex',
            alignItems: 'center',
            outline: 'none',
            border: 'none',
            fontWeight: 500,
            fontSize: '1rem',
            transition: 'background 0.2s',
          }}
          onMouseOver={e => e.currentTarget.style.backgroundColor = colors.primary.hover}
          onFocus={e => e.currentTarget.style.backgroundColor = colors.primary.hover}
          onMouseOut={e => e.currentTarget.style.backgroundColor = colors.primary.selected}
          onBlur={e => e.currentTarget.style.backgroundColor = colors.primary.selected}
        >
          {t('insurance.buttons.generatePdf', 'Generate PDF')}
        </button>
      </div>
      {patchedRenderAccordionSection({
        title: t('insurance.sections.administrative'),
        sectionKey: 'administrative',
        sectionFields: sectionFields.administrative,
        children: (
          <div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="space-y-4">
                <h3 className="font-medium text-gray-900">{t('insurance.fields.dentist')}</h3>
                <div className="grid gap-4">
                  {renderField("dentist-office", t('insurance.fields.dentistOffice'))}
                  {renderField("dentist", t('insurance.fields.dentist'))}
                  {renderField("gln-nr", t('insurance.fields.glnNr'))}
                  {renderField("rcc-nr", t('insurance.fields.rccNr'))}
                  {renderField("phone-nr", t('insurance.fields.phoneNr'))}
                  {renderField("employee-name", t('insurance.fields.employeeName'))}
                </div>
              </div>
              <div className="space-y-4">
                <h3 className="font-medium text-gray-900">{t('patients.form.name')}</h3>
                <div className="grid gap-4">
                  {/* Champ de recherche patient masqué pour le moment */}
                  {/* <div className="flex flex-col space-y-1 w-full relative">
                    <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                      {t('insurance.fields.patientSearch')}
                    </label>
                    <input
                      type="text"
                      value={patientSearch}
                      onChange={handlePatientSearchChange}
                      onFocus={() => patientSearch.length > 1 && setShowPatientDropdown(true)}
                      onBlur={() => setTimeout(() => setShowPatientDropdown(false), 150)}
                      onKeyDown={handlePatientSearchKeyDown}
                      className="rounded-md shadow-sm focus:ring focus:ring-opacity-50 mt-1 block w-full border border-gray-300 focus:border-blue-300 focus:ring-blue-200"
                      autoComplete="off"
                      placeholder={t('insurance.fields.patientSearchPlaceholder')}
                    /> */}
                    {/* Liste déroulante des patients masquée */}
                    {/* {showPatientDropdown && patientOptions.length > 0 && (
                      <ul className="absolute z-10 bg-white border border-gray-300 rounded-md mt-1 w-full max-h-56 overflow-auto shadow-lg">
                        {patientOptions.map((option, idx) => (
                          <li
                            key={option.id}
                            className={`px-4 py-2 cursor-pointer flex justify-between items-center ${
                              idx === highlightedIndex ? 'bg-blue-100' : ''
                            }`}
                            onMouseDown={() => handlePatientSelect(option)}
                            onMouseEnter={() => setHighlightedIndex(idx)}
                          >
                            <span>{option.name}</span>
                            {option.birthdate && (
                              <span className="ml-2 text-xs text-gray-500">{format(new Date(option.birthdate), 'dd.MM.yyyy')}</span>
                            )}
                          </li>
                        ))}
                      </ul>
                    )} 
                  </div>*/}
                  {renderField("patient-name", t('insurance.fields.patientName'))}
                  {renderField("patient-assurance-nr", t('insurance.fields.patientAssuranceNr'))}
                  {renderField("patient-address", t('insurance.fields.patientAddress'))}
                  {renderField("patient-birth", t('insurance.fields.patientBirth'), "date")}
                </div>
              </div>
            </div>
            {/* Separator with OR and PDF upload */}
            <div className="flex items-center my-6">
              <div className="flex-grow h-px bg-gray-300" />
              <span className="mx-4 text-gray-500 font-semibold">{t('common.or', 'or')}</span>
              <div className="flex-grow h-px bg-gray-300" />
            </div>
            <div className="mt-4 flex flex-col items-center gap-2">
              <div className="flex items-center gap-2">
                <label htmlFor="admin-pdf-upload" className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 cursor-pointer">
                  <svg className="w-4 h-4 mr-2 text-[rgb(141,212,209)]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5-5m0 0l5 5m-5-5v12" /></svg>
                  {adminPdfFile ? t('insurance.fields.replacePdf') : t('insurance.fields.uploadPdf')}
                  <input id="admin-pdf-upload" type="file" accept="application/pdf" className="hidden" onChange={handleAdminPdfUpload} />
                </label>
                {adminPdfFile && (
                  <span className="text-xs text-gray-600 bg-gray-100 px-2 py-1 rounded flex items-center gap-1">
                    {adminPdfName}
                    <button type="button" className="ml-2 text-red-500 hover:text-red-700" onClick={() => { setAdminPdfFile(null); setAdminPdfName(''); setAdminPdfBg1(null); setAdminPdfBg2(null); }}>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                  </span>
                )}
              </div>
              {adminPdfFile && <span className="text-xs text-green-600">{t('insurance.fields.pdfUploaded')}</span>}
            </div>
          </div>
        )
      })}

      {renderAccordionSection({
        title: t('insurance.sections.teethState'),
        sectionKey: 'teethState',
        sectionFields: sectionFields.teethState,
        children: (
          <div className="space-y-4">
            <p className="text-sm text-gray-600 mb-4">
              {t('insurance.fields.teethSelectorDescription')}
            </p>
            <TeethSelector 
              selectedTeeth={formData['missing-teeth'] ? formData['missing-teeth'] : ''}
              onTeethSelect={handleTeethSelection}
            />
          </div>
        )
      })}

      {renderAccordionSection({
        title: t('insurance.sections.accident'),
        sectionKey: 'accident',
        sectionFields: sectionFields.accident,
        children: (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-4">
              {renderField("accident-date", t('insurance.fields.accidentDate'), "date")}
            </div>
            <div className="space-y-4">
              {renderField("examination-date", t('insurance.fields.examinationDate'), "date")}
            </div>
            <div className="col-span-2">
              {renderField("accident-cause", t('insurance.fields.accidentCause'))}
            </div>
          </div>
        )
      })}

      {renderAccordionSection({
        title: t('insurance.sections.damage'),
        sectionKey: 'damage',
        sectionFields: sectionFields.damage,
        isOptional: true,
        children: (
          <div className="space-y-6">
            <div className="space-y-4">
              <label className="block text-sm font-medium text-gray-700">
                {t('insurance.fields.teethLost')}
              </label>
              <TeethSelector 
                selectedTeeth={formData['teeth-lost']}
                onTeethSelect={(value) => handleChange('teeth-lost', value)}
              />
            </div>

            <div className="space-y-4">
              <label className="block text-sm font-medium text-gray-700">
                {t('insurance.fields.teethMoved')}
              </label>
              <TeethSelector 
                selectedTeeth={formData['teeth-moved']}
                onTeethSelect={(value) => handleChange('teeth-moved', value)}
              />
            </div>

            <div className="space-y-4">
              <label className="block text-sm font-medium text-gray-700">
                {t('insurance.fields.teethMoving')}
              </label>
              <TeethSelector 
                selectedTeeth={formData['teeth-moving']}
                onTeethSelect={(value) => handleChange('teeth-moving', value)}
              />
            </div>

            <div className="space-y-4">
              <label className="block text-sm font-medium text-gray-700">
                {t('insurance.fields.teethHurt')}
              </label>
              <TeethSelector 
                selectedTeeth={formData['teeth-hurt']}
                onTeethSelect={(value) => handleChange('teeth-hurt', value)}
              />
            </div>

            <div className="space-y-4">
              <label className="block text-sm font-medium text-gray-700">
                {t('insurance.fields.teethFractWithoutLesion')}
              </label>
              <TeethSelector 
                selectedTeeth={formData['teeth-fract-without-lesion']}
                onTeethSelect={(value) => handleChange('teeth-fract-without-lesion', value)}
              />
            </div>

            <div className="space-y-4">
              <label className="block text-sm font-medium text-gray-700">
                {t('insurance.fields.teethFractWithLesion')}
              </label>
              <TeethSelector 
                selectedTeeth={formData['teeth-fract-with-lesion']}
                onTeethSelect={(value) => handleChange('teeth-fract-with-lesion', value)}
              />
            </div>

            <div className="space-y-4">
              <label className="block text-sm font-medium text-gray-700">
                {t('insurance.fields.teethRootFract')}
              </label>
              <TeethSelector 
                selectedTeeth={formData['teeth-root-fract']}
                onTeethSelect={(value) => handleChange('teeth-root-fract', value)}
              />
            </div>

            <div className="space-y-4">
              {renderField("soft-damage", t('insurance.fields.softDamage'), "text", false)}
            </div>

            <div className="space-y-4">
              {renderField("prosthetic-damage", t('insurance.fields.prostheticDamage'), "text", false)}
            </div>
          </div>
        )
      })}

      {renderAccordionSection({
        title: t('insurance.sections.lamal'),
        sectionKey: 'lamal',
        sectionFields: [
          'lamal-diagnostic',
          'lamal-article',
          'lamal-letter',
          'lamal-number'
        ],
        children: (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
            {/* Diagnostic - full row */}
            <div className="col-span-1 lg:col-span-2">
              {renderField("lamal-diagnostic", t('insurance.fields.lamalDiagnostic'))}
            </div>
            {/* OPAS art (left) and lettre (right) */}
            <div>
              {renderField("lamal-article", t('insurance.fields.lamalArticle'))}
            </div>
            <div>
              {renderField("lamal-letter", t('insurance.fields.lamalLetter'))}
            </div>
            {/* Chiffre (left) and checkbox (right) - both 50% */}
            <div>
              {renderField("lamal-number", t('insurance.fields.lamalNumber'))}
            </div>
            <div className="h-full">
                <div className="flex h-full items-center ">
                    <input
                        id="lamal-doctor-report"
                        type="checkbox"
                        checked={formData["lamal-doctor-report"]}
                        onChange={(e) => handleChange("lamal-doctor-report", e.target.checked)}
                        className="h-4 w-4 rounded border-gray-300 text-[rgb(141,212,209)] focus:ring-[rgb(141,212,209)]"
                    />
                    <label htmlFor="lamal-doctor-report" className="ml-2 block text-sm text-gray-900 cursor-pointer select-none">
                        {t('insurance.fields.lamalDoctorReport')}
                    </label>
                </div>
              </div>
          </div>
        )
      })}

      {renderAccordionSection({
        title: t('insurance.sections.lamalExam'),
        sectionKey: 'lamalExam',
        sectionFields: ['lamal-not-cured', 'lamal-cured', 'lamal-lesion', 'lamal-prosthetic'],
        isOptional: true,
        children: (
          <div className="space-y-6">
            <div className="space-y-4">
              <label className="block text-sm font-medium text-gray-700">
                {t('insurance.fields.lamalNotCured')}
              </label>
              <TeethSelector
                selectedTeeth={formData['lamal-not-cured']}
                onTeethSelect={(value) => handleChange('lamal-not-cured', value)}
              />
            </div>
            <div className="space-y-4">
              <label className="block text-sm font-medium text-gray-700">
                {t('insurance.fields.lamalCured')}
              </label>
              <TeethSelector
                selectedTeeth={formData['lamal-cured']}
                onTeethSelect={(value) => handleChange('lamal-cured', value)}
              />
            </div>
            <div className="space-y-4">
              <label className="block text-sm font-medium text-gray-700">
                {t('insurance.fields.lamalLesion')}
              </label>
              <TeethSelector
                selectedTeeth={formData['lamal-lesion']}
                onTeethSelect={(value) => handleChange('lamal-lesion', value)}
              />
            </div>
            <div className="space-y-4">
              {renderField('lamal-prosthetic', t('insurance.fields.lamalProsthetic'), 'text', false)}
            </div>
          </div>
        )
      })}

      {renderAccordionSection({
        title: t('insurance.sections.immediateMeasures'),
        sectionKey: 'immediateMeasures',
        sectionFields: ['measuring-diagnostic', 'measuring-others'],
        children: (
          <div className="space-y-6">
            {renderField('measuring-diagnostic', t('insurance.fields.measuringDiagnostic'), 'text', true)}
            {renderField('measuring-others', t('insurance.fields.measuringOthers'), 'text', true)}
          </div>
        )
      })}

      {renderAccordionSection({
        title: t('insurance.sections.intermediateTreatment'),
        sectionKey: 'intermediateTreatment',
        sectionFields: [
          'observation-required',
          'observation-length',
          'interm-treatment-orthodontie',
          'interm-treatment-delay-definitive',
          'interm-treatment-delay-definitive-length',
          'interm-treatment'
        ],
        isOptional: true,
        children: (
          <div className="space-y-6">
            {/* Checkbox 1: Observation nécessaire pendant ... années */}
            <div className="flex items-center gap-2">
              <input
                id="observation-required"
                type="checkbox"
                checked={formData['observation-required']}
                onChange={e => handleChange('observation-required', e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-[rgb(141,212,209)] focus:ring-[rgb(141,212,209)]"
              />
              <label htmlFor="observation-required" className="block text-sm text-gray-900 cursor-pointer select-none">
                {t('insurance.fields.observationRequired')}
              </label>
              {formData['observation-required'] && (
                <div className="ml-2 flex items-center">
                  <div style={{ width: 70 }}>
                    {renderField('observation-length', '', 'text', false)}
                  </div>
                  <span className="ml-2">{t('insurance.fields.years')}</span>
                </div>
              )}
            </div>

            {/* Checkbox 2: Traitement orthodontique... */}
            <div className="flex items-center gap-2">
              <input
                id="interm-treatment-orthodontie"
                type="checkbox"
                checked={formData['interm-treatment-orthodontie']}
                onChange={e => handleChange('interm-treatment-orthodontie', e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-[rgb(141,212,209)] focus:ring-[rgb(141,212,209)]"
              />
              <label htmlFor="interm-treatment-orthodontie" className="block text-sm text-gray-900 cursor-pointer select-none">
                {t('insurance.fields.intermTreatmentOrthodontie')}
              </label>
            </div>

            {/* Checkbox 3: Un traitement définitif... */}
            <div className="flex items-center gap-2">
              <input
                id="interm-treatment-delay-definitive"
                type="checkbox"
                checked={formData['interm-treatment-delay-definitive']}
                onChange={e => handleChange('interm-treatment-delay-definitive', e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-[rgb(141,212,209)] focus:ring-[rgb(141,212,209)]"
              />
              <label htmlFor="interm-treatment-delay-definitive" className="block text-sm text-gray-900 cursor-pointer select-none">
                {t('insurance.fields.intermTreatmentDelayDefinitive')}
              </label>
              {formData['interm-treatment-delay-definitive'] && (
                <div className="ml-2 w-32">
                  {renderField('interm-treatment-delay-definitive-length', '', 'text', false)}
                </div>
              )}
            </div>

            {/* Textarea: Proposition de traitement intermediaire */}
            <div>
              {renderField('interm-treatment', t('insurance.fields.intermTreatment'), 'textarea', false)}
            </div>
          </div>
        )
      })}

      {renderAccordionSection({
        title: t('insurance.sections.treatment'),
        sectionKey: 'treatment',
        sectionFields: sectionFields.treatment,
        isOptional: true,
        children: (
          <div className="space-y-4">
            <div>
              {renderField('definitive-treatment', t('insurance.fields.definitivetreatment'), 'textarea', false)}
            </div>
            {/* Checkbox for including quote */}
            <div className="flex items-center gap-2">
              <input
                id="include-quote"
                type="checkbox"
                checked={formData['include-quote']}
                onChange={e => handleChange('include-quote', e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-[rgb(141,212,209)] focus:ring-[rgb(141,212,209)]"
              />
              <label htmlFor="include-quote" className="block text-sm text-gray-900 cursor-pointer select-none">
                {t('insurance.fields.includeQuote')}
              </label>
            </div>
            {/* Add other treatment fields */}
          </div>
        )
      })}

      {renderAccordionSection({
        title: t('insurance.sections.replacementSchema'),
        sectionKey: 'replacementSchema',
        sectionFields: ['teeth-been-replaced'],
        isOptional: true,
        children: (
          <div className="space-y-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('insurance.fields.replacementTeethLabel')}
            </label>
            <TeethSelector
              selectedTeeth={formData['teeth-been-replaced']}
              onTeethSelect={(value) => handleChange('teeth-been-replaced', value)}
            />
          </div>
        )
      })}
    </form>
  );
});

export default InsuranceForm; 